import React, { useEffect, useState } from "react";
// import { ReactComponent as Popup } from "../../../../assets/icons/svg/Home/Create_Workspace_popup.svg"; // used in previous SVG-overlay popup
import Button from "../../../ui/buttons";
import { API_URLS } from "../../../../api/urls";
import { EnhancedTokenManager } from "../../../../utils/tokenManager";
import { DEFAULT_FONT_FAMILY } from "../../../../config/fontConfig";
import DotLoader from "../../../../assets/Animation/Dot Loader.json";
import Lottie from "lottie-react";
import {
  CreateNewWebApplicationPopupProps,
  CreateCardApiResponse,
  AccessType,
  RefreshTokenResponse,
  AuthTokens,
} from "../../../../types/feedback.types";

/**
 * API Service Class for handling authentication and API calls
 * Updated to use EnhancedTokenManager
 */
class ApiService {
  private static isRefreshing = false;
  private static failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (reason: any) => void;
  }> = [];

  private static processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });

    this.failedQueue = [];
  }

  static async refreshAccessToken(): Promise<boolean> {
    const refreshToken = EnhancedTokenManager.getRefreshToken();

    if (!refreshToken) {
      console.log("No refresh token available");
      return false;
    }

    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      console.log("Attempting to refresh access token...");
      const response = await fetch(API_URLS.REFRESH_TOKEN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      });

      console.log("Refresh token response status:", response.status);

      if (response.ok) {
        const data: RefreshTokenResponse = await response.json();
        console.log("Token refresh successful");

        // Update stored tokens using EnhancedTokenManager
        const newTokens: AuthTokens = {
          access_token: data.access_token,
          refresh_token: data.refresh_token || refreshToken,
          token_type: data.token_type || "bearer",
        };

        EnhancedTokenManager.setTokens(newTokens);
        this.processQueue(null, data.access_token);

        return true;
      } else {
        const errorData = await response.json();
        console.error("Refresh token failed:", errorData);

        // Clear invalid tokens
        EnhancedTokenManager.performLogout();
        this.processQueue(errorData, null);

        return false;
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      this.processQueue(error, null);
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  static async makeAuthenticatedRequest(
    url: string,
    options: RequestInit = {},
  ): Promise<Response> {
    const accessToken = EnhancedTokenManager.getAccessToken();

    if (!accessToken) {
      throw new Error("No access token available. Please log in again.");
    }

    console.log(`Making authenticated request to: ${url}`);

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    };

    let response = await fetch(url, {
      ...options,
      headers,
    });

    console.log(`Initial response status: ${response.status}`);

    // If unauthorized, try to refresh token
    if (response.status === 401) {
      console.log("Received 401, attempting token refresh...");

      const refreshed = await this.refreshAccessToken();

      if (refreshed) {
        const newAccessToken = EnhancedTokenManager.getAccessToken();
        console.log(
          "Token refreshed successfully, retrying original request...",
        );

        // Retry the original request with new token
        response = await fetch(url, {
          ...options,
          headers: {
            ...headers,
            Authorization: `Bearer ${newAccessToken}`,
          },
        });

        console.log(`Retry response status: ${response.status}`);
      } else {
        throw new Error("Authentication failed. Please log in again.");
      }
    }

    return response;
  }

  /**
   * Creates a feedback card via API with dynamic workspace ID
   * @param feedbackData - The feedback data to send to the API
   * @param workspaceId - The workspace ID to create the card in
   * @returns Promise<CreateCardApiResponse | null>
   */
  static async createFeedbackCard(
    feedbackData: {
      name: string;
      collaborators: string[];
      accessType: AccessType;
    },
    workspaceId: number,
  ): Promise<CreateCardApiResponse | null> {
    try {
      // Build the API URL dynamically with the workspace ID
      const apiUrl = `${API_URLS.CREATE_CARD_BASE}?workspace_id=${workspaceId}`;

      console.log("🚀 API Request URL:", apiUrl);
      console.log("🚀 Workspace ID:", workspaceId);
      console.log("🚀 Request payload:", {
        name: feedbackData.name,
        collaborators: feedbackData.collaborators,
        access_type: feedbackData.accessType,
      });

      const response = await this.makeAuthenticatedRequest(apiUrl, {
        method: "POST",
        body: JSON.stringify({
          name: feedbackData.name,
          collaborators: feedbackData.collaborators,
          access_type: feedbackData.accessType,
        }),
      });

      console.log("🚀 API Response status:", response.status);

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = `HTTP ${response.status}: ${errorText}`;
          }
          console.error("🚨 API Error Response:", errorText);
        } catch (e) {
          // If we can't read the error text, use the default message
        }

        // Handle specific error cases
        if (response.status === 401) {
          errorMessage =
            "Authentication failed. Please check your access token.";
        } else if (response.status === 403) {
          errorMessage =
            "Access denied. You do not have permission to create feedback cards.";
        } else if (response.status === 404) {
          errorMessage = "Workspace not found. Please check the workspace ID.";
        } else if (response.status >= 500) {
          errorMessage = "Server error. Please try again later.";
        }

        throw new Error(errorMessage);
      }

      const apiResponse: CreateCardApiResponse = await response.json();
      console.log("✅ API Success Response:", apiResponse);
      return apiResponse;
    } catch (error) {
      console.error("🚨 Error creating feedback card:", error);
      throw error;
    }
  }
}

/**
 * Validates if the input contains URL-like patterns
 * Returns true if the input appears to contain a URL
 */
const containsUrl = (text: string): boolean => {
  // Pattern to detect URLs including:
  // - http:// or https://
  // - www.
  // - domain.extension patterns
  // - Common TLDs
  const urlPatterns = [
    /https?:\/\//i, // http:// or https://
    /www\./i, // www.
    /[a-zA-Z0-9-]+\.(com|org|net|edu|gov|io|co|ai|dev|app|tech|info|biz|me|xyz|online|site|website|store|shop)[\/\s]?/i, // domain.tld
  ];

  return urlPatterns.some((pattern) => pattern.test(text));
};

/**
 * Validates if the input contains special characters.
 * Allowed: letters, numbers, spaces, hyphens (-), underscores (_)
 * Returns true if any disallowed character is present.
 */
const containsSpecialCharacters = (text: string): boolean => {
  return /[^a-zA-Z0-9 _-]/.test(text);
};

/**
 * CreateNewWebApplicationPopup Component
 *
 * A modal popup for creating new web feedback with collaboration settings.
 * Allows users to:
 * - Name their feedback
 * - Add collaborators via email
 * - Set access permissions (comment or edit)
 * - Creates feedback card via API with dynamic workspace ID and refresh token support
 * - Uses EnhancedTokenManager for improved user session management
 * - Shows error message for duplicate project names with red border
 */
const CreateNewWebApplicationPopup: React.FC<
  CreateNewWebApplicationPopupProps
> = ({ isOpen, onClose, onContinue, workspaceId }) => {
  // State management for form inputs
  const [feedbackName, setFeedbackName] = useState<string>("");
  const [collaboratorEmails, setCollaboratorEmails] = useState<string>("");
  const [accessType, setAccessType] = useState<AccessType>("comment");
  const [isAccessDropdownOpen, setIsAccessDropdownOpen] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [nameError, setNameError] = useState<string>("");

  // Ref for input auto-focus
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Enhanced authentication check using EnhancedTokenManager
  useEffect(() => {
    if (isOpen) {
      const accessToken = EnhancedTokenManager.getAccessToken();
      const refreshToken = EnhancedTokenManager.getRefreshToken();
      const currentUserId = EnhancedTokenManager.getCurrentUserId();
      const userWorkspaceId =
        EnhancedTokenManager.getWorkspaceIdForCurrentUser();

      console.log("CreateNewWebApplicationPopup - Authentication check:", {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        currentUserId,
        userWorkspaceId,
        propWorkspaceId: workspaceId,
      });

      if (!accessToken || !refreshToken) {
        console.warn(
          "No authentication tokens found. User may need to log in again.",
        );
        setError("Authentication required. Please log in again.");
        return;
      }

      if (!currentUserId) {
        console.warn("Could not determine current user from token");
        setError("Invalid authentication token. Please log in again.");
        return;
      }

      // Enhanced workspace validation
      if (!userWorkspaceId && !workspaceId) {
        console.warn("No workspace found for current user");
        setError("No workspace found. Please complete setup.");
        return;
      }

      // Check if the provided workspaceId matches the user's workspace
      if (userWorkspaceId && workspaceId && userWorkspaceId !== workspaceId) {
        console.warn(
          `Workspace ID mismatch - user workspace: ${userWorkspaceId}, prop workspace: ${workspaceId}`,
        );
        setError("Workspace access denied. Please check your permissions.");
        return;
      }

      // All validations passed
      console.log("Authentication and workspace validation successful");
      setError(""); // Clear any previous auth errors
    }
  }, [isOpen, workspaceId]);

  /**
   * Enhanced continue handler with better error handling and user validation
   * Now includes duplicate name detection
   */

  const handleContinue = async (): Promise<void> => {
    if (feedbackName.trim().length < 1) {
      setNameError("Please enter at least 1 character.");
      return;
    }

    // Clear previous name error
    setNameError("");

    // NEW: Check for URLs in the feedback name
    if (containsUrl(feedbackName)) {
      setNameError(
        "Project name cannot contain URLs. Please enter a valid project name.",
      );
      return;
    }

    // Check for special characters in the feedback name
    if (containsSpecialCharacters(feedbackName)) {
      setNameError(
        "Project name cannot contain special characters. Only letters, numbers, spaces, hyphens (-) and underscores (_) are allowed.",
      );
      return;
    }

    // Enhanced pre-flight checks
    const currentUserId = EnhancedTokenManager.getCurrentUserId();
    const userWorkspaceId = EnhancedTokenManager.getWorkspaceIdForCurrentUser();

    if (!currentUserId) {
      setError("User session invalid. Please log in again.");
      return;
    }

    // Use user's workspace ID if available, otherwise fall back to prop
    const effectiveWorkspaceId = userWorkspaceId || workspaceId;

    if (!effectiveWorkspaceId) {
      setError("No workspace available. Please complete setup.");
      return;
    }

    console.log(
      "Creating feedback card for user:",
      currentUserId,
      "workspace:",
      effectiveWorkspaceId,
    );

    const feedbackData = {
      name: feedbackName.trim(),
      collaborators: collaboratorEmails
        .split(" ")
        .map((email) => email.trim())
        .filter((email) => email.length > 0), // Remove empty strings
      accessType,
    };

    setIsLoading(true);
    setError("");

    try {
      // Call the API to create the feedback card with the effective workspace ID
      const apiResponse = await ApiService.createFeedbackCard(
        feedbackData,
        effectiveWorkspaceId,
      );

      if (apiResponse) {
        console.log("Feedback card created successfully:", apiResponse);

        // ✅ REMOVED: No need for sessionStorage flag
        console.log("🎯 New project created - ID:", apiResponse.id);

        // Success - call the onContinue callback with both form data and API response
        onContinue(feedbackData, apiResponse);
        onClose();
      }
    } catch (error) {
      // Enhanced error handling with user-specific context
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to create feedback card";

      console.error(
        "Error creating feedback card for user:",
        currentUserId,
        error,
      );

      // Handle duplicate name error (check if error message indicates duplicate)
      if (
        errorMessage.includes("already exists") ||
        errorMessage.includes("duplicate") ||
        errorMessage.includes("name is already in use") ||
        errorMessage.toLowerCase().includes("already exist")
      ) {
        setNameError(
          "A project with this name already exists. Please choose a different name.",
        );
      }
      // Handle authentication errors specifically
      else if (
        errorMessage.includes("Authentication failed") ||
        errorMessage.includes("Please log in again") ||
        errorMessage.includes("Invalid authentication token")
      ) {
        setError("Your session has expired. Please log in again.");
        // Could trigger logout or redirect here if needed
      } else if (errorMessage.includes("Workspace not found")) {
        setError("Workspace not found. Please check your access permissions.");
      } else if (errorMessage.includes("Access denied")) {
        setError(
          "Access denied. You don't have permission to create feedback in this workspace.",
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Enhanced form reset with debug info
   */
  const resetForm = (): void => {
    setFeedbackName("");
    setCollaboratorEmails("");
    setAccessType("comment");
    setIsAccessDropdownOpen(false);
    setError("");
    setNameError("");
    console.log("Form reset completed");
  };

  /**
   * Resets form state when modal opens
   * Ensures clean state for each new feedback creation
   */
  useEffect(() => {
    if (isOpen) {
      resetForm();
      // Debug current user context when opening
      const currentUserId = EnhancedTokenManager.getCurrentUserId();
      const userWorkspaceId =
        EnhancedTokenManager.getWorkspaceIdForCurrentUser();
      console.log("Opening CreateNewWebApplicationPopup for user:", {
        currentUserId,
        userWorkspaceId,
        propWorkspaceId: workspaceId,
      });

      // Auto-focus the input field with a small delay to ensure DOM is ready
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, workspaceId]);

  /**
   * Enhanced discard handler with cleanup
   */
  const handleDiscard = (): void => {
    console.log("Discarding feedback creation");
    resetForm();
    onClose();
  };

  // Don't render if modal is closed
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-[10px] shadow-xl max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-lg 2xl:max-w-xl 4xl:max-w-2xl w-full  overflow-hidden"
        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
      >
        {/* Header */}
        <div className="px-4 pt-4 pb-2">
          <h2 className="text-[18px] 3xl:text-[20px] font-medium text-[#1E1F26]">
            Create file
          </h2>
        </div>

        <hr className="border-gray-200" />

        {/* Body */}
        <div className="px-4 py-5">
          <label
            className="block text-sm 3xl:text-[16px] text-[#1E1F26] mb-2"
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            What's the name of your file?
          </label>
          <input
            ref={inputRef}
            type="text"
            placeholder="Untitled project"
            value={feedbackName}
            onChange={(e) => {
              const newValue = e.target.value;
              setFeedbackName(newValue);
              if (nameError) setNameError("");
              if (newValue.trim() && containsUrl(newValue)) {
                setNameError("Project name cannot contain URLs.");
              } else if (newValue.trim() && containsSpecialCharacters(newValue)) {
                setNameError(
                  "Project name cannot contain special characters. Only letters, numbers, spaces, hyphens (-) and underscores (_) are allowed.",
                );
              }
            }}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                feedbackName.trim().length >= 1 &&
                !isLoading
              ) {
                handleContinue();
              }
            }}
            className={`w-full px-3 py-2 border-[1px] ${
              nameError ? "border-red-500" : "border-[#66727F]"
            } rounded-md text-sm 3xl:text-[16px] text-[#1E1F26] font-inter font-medium placeholder-gray-400 bg-white focus:outline-none focus:border-[#49AFFF]`}
            aria-label="Feedback name"
            autoFocus
          />
          {nameError && (
            <p
              className="mt-2 text-sm 3xl:text-base text-red-500 font-medium"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              {nameError}
            </p>
          )}
          {error && (
            <p
              className="mt-2 text-sm 3xl:text-base text-red-500 font-medium"
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              {error}
            </p>
          )}
        </div>

        <hr className="border-gray-200 mt-2" />

        {/* Footer */}
        <div className="px-4 py-4 flex justify-end gap-3">
          <Button variant="cancel" size="lg" onClick={handleDiscard} disabled={isLoading} className="w-full sm:w-auto text-sm" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>
            Cancel
          </Button>
          <Button variant={feedbackName.trim().length < 1 ? "secondary" : "primary"} size="lg" onClick={handleContinue} disabled={isLoading} className="w-full sm:w-auto text-sm relative overflow-visible" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>
            {isLoading ? (
              <span className="inline-block relative" style={{ width: 40, height: 16 }}>
                <Lottie animationData={DotLoader} loop={true} style={{ width: 120, height: 48, position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />
              </span>
            ) : (
              "Create"
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  /* --- Previous SVG-overlay popup (commented out) ---
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ">
      <div className="bg-white rounded-lg shadow-xl max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-2xl 2xl:max-w-4xl 4xl:max-w-6xl overflow-hidden m-0 p-0">
        <div className="relative w-full h-full m-0 p-0">
          <Popup className="w-full h-auto block rounded-t-lg" />
          <div className="absolute top-[14%] left-[18%] right-[4%]">
            <input
              ref={inputRef}
              type="text"
              placeholder="Untitled project"
              value={feedbackName}
              onChange={(e) => {
                const newValue = e.target.value;
                setFeedbackName(newValue);
                if (nameError) setNameError("");
                if (newValue.trim() && containsUrl(newValue)) {
                  setNameError("Project name cannot contain URLs.");
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && feedbackName.trim().length >= 1 && !isLoading) {
                  handleContinue();
                }
              }}
              className={`w-full px-3 py-2 border-[1.5px] ${
                nameError ? "border-red-500" : "border-[#1E1F26]"
              } rounded-md text-lg 3xl:text-xl text-[#1E1F26] font-inter font-medium placeholder-gray-400 bg-white focus:outline-none focus:ring-1 focus:ring-[#7ABFFF] focus:border-transparent`}
              required
              aria-label="Feedback name"
              autoFocus
            />
            {nameError && (
              <p className="mt-2 text-sm 3xl:text-base text-red-500 font-medium" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>
                {nameError}
              </p>
            )}
          </div>
        </div>
        <footer className="p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-end border-t border-gray-200">
          <Button variant="cancel" size="lg" onClick={handleDiscard} disabled={isLoading} className="w-full sm:w-auto text-sm" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>
            Discard
          </Button>
          <Button variant="primary" size="lg" onClick={handleContinue} disabled={feedbackName.trim().length < 1} className="w-full sm:w-auto text-sm relative overflow-visible" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>
            {isLoading ? (
              <span className="inline-block relative" style={{ width: 40, height: 16 }}>
                <Lottie animationData={DotLoader} loop={true} style={{ width: 120, height: 48, position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />
              </span>
            ) : (
              "Create"
            )}
          </Button>
        </footer>
      </div>
    </div>
  );
  --- End previous popup --- */
};

export default CreateNewWebApplicationPopup;
